import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Stack,
  CircularProgress
} from '@mui/material';

export type ConfirmByTypingDialogProps = {
  open: boolean;
  expectedText: string;                // what the user must type to enable confirm
  title?: string;
  message?: React.ReactNode;          // extra instructions/description
  confirmLabel?: string;              // defaults to "Delete"
  cancelLabel?: string;               // defaults to "Cancel"
  loading?: boolean;                  // disable buttons + show spinner while confirming
  caseSensitive?: boolean;            // default true
  autoFocus?: boolean;                // default true
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  normalize?: (s: string) => string;  // optional custom normalizer if you want e.g. trim+lower+collapse spaces
};

export function ConfirmByTypingDialog({
                                        open,
                                        expectedText,
                                        title = "Confirm",
                                        message,
                                        confirmLabel = "Delete",
                                        cancelLabel = "Cancel",
                                        loading = false,
                                        caseSensitive = true,
                                        autoFocus = true,
                                        onClose,
                                        onConfirm,
                                        normalize,
                                      }: ConfirmByTypingDialogProps) {
  const [input, setInput] = useState("");

  useEffect(() => {
    if (open) setInput("");
  }, [open]);

  const norm = (s: string) =>
    normalize ? normalize(s) : s.trim();

  const matches = React.useMemo(() => {
    const a = norm(input);
    const b = norm(expectedText);
    return caseSensitive ? a === b : a.toLowerCase() === b.toLowerCase();
  }, [input, expectedText, caseSensitive, normalize]);

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!matches || loading) return;
    await onConfirm();
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="sm">
      <form onSubmit={handleSubmit}>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {message && (
              <Typography variant="body2" color="text.secondary">
                {message}
              </Typography>
            )}
            <Typography variant="body2">
              Type <strong>{expectedText}</strong> to confirm:
            </Typography>
            <TextField
              fullWidth
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type exact text"
              autoFocus={autoFocus}
              disabled={loading}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>{cancelLabel}</Button>
          <Button
            type="submit"
            variant="contained"
            color="error"
            disabled={!matches || loading}
            startIcon={loading ? <CircularProgress size={18} /> : undefined}
          >
            {confirmLabel}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
